import React from 'react';
import { format } from 'date-fns';
import { css } from '@emotion/react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { renderActivityLog } from '@onward-delivery/core';
import { colors } from '@/styles';
import { SecondaryButton } from '@/styles/blocks';
import { getEntityLabel } from './constants';
import { getActivityLogSource } from './source';

/** Label shown in each activity log row's menu to open the full entry. */
export const VIEW_DETAILS_LABEL = 'View details';

const listStyles = css`
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: 8px 16px;
    margin: 0;
    & dt {
        color: ${colors.greys.primary};
        font-size: 13px;
    }
    & dd {
        margin: 0;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
    }
`;

/**
 * Read-only dialog with one activity log row in full. The table cuts Details to one line, so long entries (such as
 * an order split) are read here.
 *
 * @param {{ log: object|null, tz?: string, onClose: () => void }} props `log` is the row from the activity log query;
 *   the dialog is open while it is set. `tz` formats typed timestamps, as in the table.
 */
export const ActivityLogDetailsDialog = ({ log, tz, onClose }) => {
    const rendered = log ? renderActivityLog(log, { tz }) : null;
    const source = log ? getActivityLogSource(log) : null;
    const rows = log
        ? [
              ['Date', log.timestamp ? format(new Date(log.timestamp), 'MMM d, yyyy h:mm a') : '—'],
              ['Description', rendered?.message ?? log.log_message ?? '—'],
              ['Details', rendered?.detail ?? log.log_detail ?? '—'],
              ['Entity', getEntityLabel(log)],
              ['Source', source?.label || '—'],
          ]
        : [];

    return (
        <Dialog open={!!log} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="activity-log-details-title">
            <DialogTitle id="activity-log-details-title">{rendered?.message ?? log?.log_message}</DialogTitle>
            <DialogContent>
                <dl css={listStyles} data-testid="activity-log-details">
                    {rows.map(([label, value]) => (
                        <React.Fragment key={label}>
                            <dt>{label}</dt>
                            <dd>{value || '—'}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            </DialogContent>
            <DialogActions>
                <SecondaryButton onClick={onClose}>Close</SecondaryButton>
            </DialogActions>
        </Dialog>
    );
};

export default ActivityLogDetailsDialog;
